'use client';

import React, { useState } from 'react';
import Button from '@/ui/components/shared/Button';
import { useTranslations } from 'next-intl';
import { useCaptchaHook } from '@aacn.eu/use-friendly-captcha';
import { FRIENDLY_CAPTCHA_ENDPOINT } from '@/integrations/captcha/constants';
import { useLocalization } from '@/ui/providers/localizationProvider';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import TextInput from '@/ui/components/shared/inputs/TextInput';
import PrivacyPolicyCheckBox from '@/ui/components/tiles/ClubRegistration/components/PrivacyPolicyCheckBox';
import { useSearchParams } from 'next/navigation';
import axios from 'axios';
import ErrorCodeEnum from '@/enums/error-code-enum';
import { buildTrackingId } from '@/utils/tracking-helper';
import {
  ActivationCodeSent,
  ActivationCodeSentFormData,
} from './nuki-premium-activation-code-sent';

type NukiPremiumActivationFormProps = {
  trackingPrefix?: string;
};

export function NukiPremiumActivationForm({
  trackingPrefix,
}: Readonly<NukiPremiumActivationFormProps>) {
  const { language } = useLocalization();
  const t = useTranslations('NukiPremiumActivationCodeForm');

  const [submissionFailedError, setSubmissionFailedError] = useState('');
  const [isSubmissionSuccess, setIsSubmissionSuccess] = useState(false);
  const searchParams = useSearchParams();
  const deviceId = searchParams.get('deviceId') || '';

  const formSchema = yup.object({
    firstName: yup.string().required(t('formRequiredFirstName')).max(50, t('formMaxLength50Chars')),
    lastName: yup.string().required(t('formRequiredLastName')).max(50, t('formMaxLength50Chars')),
    email: yup.string().email(t('formInvalidEmail')).required(t('formRequiredEmail')),
    privacyPolicy: yup
      .boolean()
      .oneOf([true], t('formRequiredPrivacyPolicy'))
      .required(t('formRequiredPrivacyPolicy')),
    deviceId: yup.string().required(t('formErrorUnknownDevice')),
    language: yup.string(),
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    getValues,
  } = useForm<yup.InferType<typeof formSchema>>({
    resolver: yupResolver(formSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      privacyPolicy: false,
      deviceId,
      language,
    },
  });

  let generalError = '';
  if (Object.keys(errors).length && errors?.deviceId?.message) {
    generalError = t(errors?.deviceId?.message);
  } else if (submissionFailedError) {
    generalError = submissionFailedError;
  }

  const captchaHook = useCaptchaHook({
    siteKey: process.env.NEXT_PUBLIC_FRIENDLY_CAPTCHA_SITE_KEY || '',
    endpoint: FRIENDLY_CAPTCHA_ENDPOINT,
    startMode: 'auto',
    language: language as any,
    showAttribution: true,
  });

  const onSubmit = async (data: yup.InferType<typeof formSchema>) => {
    setSubmissionFailedError('');

    try {
      const parsedData = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        frcCaptchaSolution: captchaHook.captchaStatus.solution as string,
        deviceId: data.deviceId,
        language,
      };

      await axios.post('/api/subscriptions/nuki-premium/activation-code', parsedData, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      setIsSubmissionSuccess(true);
    } catch (error: any) {
      const responseData = await error?.response?.data;
      const errorCode = responseData?.errorCode;
      let errorMessage = '';

      switch (errorCode) {
        case ErrorCodeEnum.NUKI_PREMIUM_LIFETIME_DEVICE_ALREADY_HAS_ACTIVE_SUBSCRIPTION:
          errorMessage = t('formErrorDeviceAlreadyHasActiveSubscription');
          break;
        case ErrorCodeEnum.NUKI_PREMIUM_LIFETIME_DEVICE_INVALID:
          errorMessage = t('formErrorDeviceInvalid');
          break;
        case ErrorCodeEnum.NUKI_PREMIUM_LIFETIME_DEVICE_REGION_UNSUPPORTED:
          errorMessage = t('formErrorDeviceRegionUnsupported');
          break;
        case ErrorCodeEnum.NUKI_PREMIUM_LIFETIME_CAPTCHA_VALIDATION_FAILED:
          errorMessage = t('formErrorCaptchaValidationFailed');
          break;
        default:
          errorMessage = t('formErrorSubmissionFailed');
          break;
      }

      captchaHook.resetWidget();
      setSubmissionFailedError(errorMessage);
    }
  };

  if (isSubmissionSuccess) {
    return <ActivationCodeSent formData={getValues() as ActivationCodeSentFormData} />;
  }

  return (
    <div className="flex justify-center">
      <form className="grid max-w-[56rem] grid-cols-1 gap-y-6" onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-2 sm:mb-8">
          <h1 className="mb-3 text-center text-[1.625em] font-bold sm:text-[3em]">
            {t('formTitle')}
          </h1>
          <h2 className="text-center text-md leading-6 sm:text-2xl">{t('formSubtitle')}</h2>
        </div>
        <div className="grid gap-2 sm:grid-cols-2 md:gap-8">
          <div>
            <TextInput
              id="firstName"
              register={register}
              errors={errors.firstName}
              label={t('formLabelFirstName')}
              placeholder={t('formLabelFirstName')}
              required
            />
          </div>
          <div>
            <TextInput
              id="lastName"
              register={register}
              errors={errors.lastName}
              label={t('formLabelLastName')}
              placeholder={t('formLabelLastName')}
              required
            />
          </div>
        </div>

        <div>
          <TextInput
            id="email"
            register={register}
            errors={errors.email}
            label={t('formLabelEmail')}
            placeholder={t('formPlaceholderEmail')}
            required
            type="email"
          />
        </div>

        <div className="space-y-4">
          <PrivacyPolicyCheckBox register={register} errors={errors.privacyPolicy} />
        </div>

        <div>
          <div className="inline-block min-w-min">
            {captchaHook.CaptchaWidget({ className: 'relative' })}
          </div>
        </div>

        <div>{generalError && <p className="text-red">{generalError}</p>}</div>

        <div>
          <input {...register('deviceId')} id="deviceId" name="deviceId" type="hidden" />

          <Button
            type="submit"
            className="py-2"
            disabled={isSubmitting}
            loading={isSubmitting}
            data-tile-element-id={buildTrackingId(trackingPrefix, 'submit')}
          >
            {t('formSubmitBtn')}
          </Button>
        </div>
      </form>
    </div>
  );
}
